import { useState, useCallback, useEffect } from 'react';
import type { Directory } from '@aidkitorg/aidkit/lib/directory';
import { extractReturnAsJson } from '@aidkitorg/types/lib/endpoints';
import { Button, Spinner } from '@aidkitorg/component-library';
import { usePost } from '../API';
import {
  ExportDate,
  ProgramImagesExportDownload,
  ProgramImagesExportLostFiles as LostFiles,
  ProgramImagesExportProgress as Progress,
} from '../Components/ProgramImagesExportDownload';

type ProgramExport = NonNullable<extractReturnAsJson<Directory['/admin/get_program_images_export_status']>['programExport']>;

const POLL_MS = 30_000;
// Mirrors the server's staleness window: a batch that hasn't checked in for this long is dead.
const STALE_MS = 20 * 60 * 1000;

export function ProgramImagesExportPage() {
  return <div className="flex flex-col">
    <div className="mb-40" style={{ maxWidth: "800px", padding: "10px" }}>
      <ProgramImagesExportPageContents />
    </div>
  </div>
};

function ProgramImagesExportPageContents() {
  const [programExport, setProgramExport] = useState<ProgramExport | null | undefined>();
  const getStatus = usePost('/admin/get_program_images_export_status');

  const refresh = useCallback(async () => {
    const response = await getStatus({});
    if (response && !('error' in response)) {
      setProgramExport(response.programExport);
    }
  }, [getStatus]);

  useEffect(() => { refresh(); }, [refresh]);

  const inProgress = programExport?.status === 'enqueued' || programExport?.status === 'running';
  useEffect(() => {
    if (!inProgress) return;
    const id = setInterval(refresh, POLL_MS);
    return () => clearInterval(id);
  }, [inProgress, refresh]);

  if (programExport === undefined) {
    return <Spinner />;
  }
  if (!programExport) {
    return <ScheduleSection refresh={refresh} />;
  }
  return <div className="space-y-4">
    <p>
      This export was started by {programExport.createdBy} on{' '}
      <ExportDate value={programExport.createdAt} />.
    </p>
    {inProgress && <InProgressSection programExport={programExport} refresh={refresh} />}
    {programExport.status === 'failed' && <FailedSection programExport={programExport} refresh={refresh} />}
    {programExport.status === 'completed' && <CompletedSection programExport={programExport} refresh={refresh} />}
  </div>;
}

function ScheduleSection({ refresh }: { refresh: () => Promise<void> }) {
  const schedule = usePost('/admin/schedule_program_images_export');
  const [busy, setBusy] = useState(false);

  const handleClick = useCallback(async () => {
    setBusy(true);
    const response = await schedule({});
    if (response && !('error' in response)) await refresh();
    setBusy(false);
  }, [schedule, refresh]);

  return <div className="space-y-4">
    <p>
      Export every uploaded document for this program as a set of zip files, one folder per applicant.
      Large programs take hours or days; you can leave this page and come back to check progress.
    </p>
    <Button onClick={handleClick} loading={busy} isDisabled={busy}>Export program images</Button>
  </div>;
}


function InProgressSection({ programExport, refresh }: { programExport: ProgramExport, refresh: () => Promise<void> }) {
  const stale = Date.now() - programExport.updatedAt > STALE_MS;
  return <div className="space-y-4">
    <p>This export is running. Progress updates every few minutes.</p>
    <Progress programExport={programExport} />
    <div className="flex gap-2">
      <Button variant="secondary" onClick={refresh}>Refresh</Button>
      {stale && <RetryButton refresh={refresh} />}
    </div>
    {stale && <p className="text-sm text-gray-600">
      This export hasn't made progress in over 20 minutes and has probably stopped. Retrying resumes where it left off.
    </p>}
  </div>;
}

function FailedSection({ programExport, refresh }: { programExport: ProgramExport, refresh: () => Promise<void> }) {
  return <div className="space-y-4">
    <p>This export failed. Retrying resumes where it left off.</p>
    {programExport.lastError && <pre className="text-sm whitespace-pre-wrap bg-gray-50 p-2 rounded">{programExport.lastError}</pre>}
    <Progress programExport={programExport} />
    <LostFiles programExport={programExport} />
    <div className="flex gap-2">
      <RetryButton refresh={refresh} />
      <DeleteButton refresh={refresh} />
    </div>
  </div>;
}

function RetryButton({ refresh }: { refresh: () => Promise<void> }) {
  const retry = usePost('/admin/retry_program_images_export');
  const [busy, setBusy] = useState(false);
  const handleClick = useCallback(async () => {
    setBusy(true);
    await retry({});
    await refresh();
    setBusy(false);
  }, [retry, refresh]);
  return <Button onClick={handleClick} loading={busy} isDisabled={busy}>Retry</Button>;
}

function DeleteButton({ refresh }: { refresh: () => Promise<void> }) {
  const remove = usePost('/admin/delete_program_images_export');
  const [busy, setBusy] = useState(false);
  const handleClick = useCallback(async () => {
    if (!window.confirm('Delete this export? The zip files already created stay in storage, but you will need to start a new export to download them from here.')) return;
    setBusy(true);
    await remove({});
    await refresh();
    setBusy(false);
  }, [remove, refresh]);
  return <Button variant="danger" onClick={handleClick} loading={busy} isDisabled={busy}>Delete export</Button>;
}

function CompletedSection({ programExport, refresh }: { programExport: ProgramExport, refresh: () => Promise<void> }) {
  const getPartUrlPost = usePost('/admin/get_program_images_export_part_url');
  const getPartUrl = useCallback((part: number) => getPartUrlPost({ part }), [getPartUrlPost]);
  const { parts } = programExport;

  return <div className="space-y-4">
    <p>{parts.length === 0 ? 'This export is complete, but no documents were found for this program.' : 'This export is complete.'}</p>
    <Progress programExport={programExport} />
    <LostFiles programExport={programExport} />
    {parts.length > 0 && <ShareLinkSection />}
    {parts.length > 0 && <p className="font-medium">Or download the files yourself</p>}
    <ProgramImagesExportDownload programExport={programExport} getPartUrl={getPartUrl}>
      <DeleteButton refresh={refresh} />
    </ProgramImagesExportDownload>
  </div>;
}

function ShareLinkSection() {
  const createLink = usePost('/admin/create_program_images_export_share_link');
  const [busy, setBusy] = useState(false);
  const [link, setLink] = useState<{ url: string, expires: number } | null>(null);
  const [copied, setCopied] = useState(false);

  const handleCreate = useCallback(async () => {
    setBusy(true);
    const response = await createLink({});
    if (response && !('error' in response)) {
      setLink(response);
      setCopied(false);
    }
    setBusy(false);
  }, [createLink]);

  const handleCopy = useCallback(async () => {
    if (!link) return;
    await navigator.clipboard.writeText(link.url);
    setCopied(true);
  }, [link]);

  return <div className="border rounded p-3 space-y-3">
    <p className="font-medium">Share with the partner</p>
    <p className="text-sm">
      Generate a link that lets anyone with a login for this program download these files for the next 7 days,
      without giving them access to anything else. Treat the link like a password; anyone you give it to can pass it on.
    </p>
    <Button onClick={handleCreate} loading={busy} isDisabled={busy}>
      {link ? 'Generate a new link' : 'Generate share link'}
    </Button>
    {link && <div className="space-y-2">
      <div className="flex gap-2 items-center">
        <input readOnly value={link.url} className="flex-1 font-mono text-xs border rounded px-2 py-1" onFocus={e => e.target.select()} />
        <Button variant="secondary" onClick={handleCopy}>{copied ? 'Copied' : 'Copy'}</Button>
      </div>
      <p className="text-sm text-gray-600">
        Expires <ExportDate value={link.expires} className="text-sm" />.
        Deleting and re-running the export also invalidates it.
      </p>
    </div>}
  </div>;
}
